import { getAuthHeaders, getBaseUrl } from './apiClient';

export type InventoryEntry = {
  url: string;
  wp_id: number;
  content_type: 'post' | 'page';
  slug: string;
  title: string;
  author: string;
  category: string;
  category_slug: string;
  published_at: string;
  modified_at: string;
  word_count: number;
  headings: string[];
  excerpt: string;
  enrichment: {
    source: string;
    primary_topic: string;
    keywords: string[];
    icp: string;
    funnel_stage: string;
    content_pillar: string;
    summary: string;
  } | null;
  pipeline: {
    candidate_id: string;
    run_id: string;
    primary_keyword: string;
    secondary_keywords: string[];
    seo_score: number | null;
  } | null;
  synced_at: string;
};

export type SyncStatus = {
  status: 'running' | 'completed' | 'failed' | 'idle';
  updated_at: string;
  deadline?: string;
  step?: string;
  new?: number;
  modified?: number;
  enriched?: number;
  enriched_so_far?: number;
  enriching_total?: number;
  total?: number;
  error?: string;
};

export type InventoryStatus = {
  total: number;
  posts: number;
  pages: number;
  enriched: number;
  last_synced: string;
  sync: SyncStatus | null;
};

export type SyncResult = {
  status: string;
  new: number;
  modified: number;
  unchanged?: number;
  removed?: number;
  enriched: number;
  total: number;
};

export type PendingArticle = {
  wp_id: number;
  title: string;
  link: string;
  modified: string;
  content_type: 'post' | 'page';
};

export type PendingCheck = {
  high_water_mark: string | null;
  pending_count: number;
  pending_articles: PendingArticle[];
  checked_at: string;
  message?: string;
};

export const contentInventoryService = {
  async list(contentType?: string, categorySlug?: string): Promise<{ entries: InventoryEntry[]; count: number }> {
    const params = new URLSearchParams();
    if (contentType) params.set('content_type', contentType);
    if (categorySlug) params.set('category_slug', categorySlug);
    const qs = params.toString() ? `?${params}` : '';
    const resp = await fetch(`${getBaseUrl()}/secure/blog/content-inventory${qs}`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to list inventory (${resp.status})`);
    return await resp.json();
  },

  async status(): Promise<InventoryStatus> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/content-inventory/status`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to get inventory status (${resp.status})`);
    return await resp.json();
  },

  async pendingCheck(): Promise<PendingCheck> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/content-inventory/pending`, { headers: await getAuthHeaders() });
    if (!resp.ok) throw new Error(`Failed to check pending updates (${resp.status})`);
    return await resp.json();
  },

  async updateEnrichment(url: string, fields: { content_pillar?: string; funnel_stage?: string }): Promise<{ url: string; enrichment: any }> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/content-inventory/${encodeURIComponent(url)}`, {
      method: 'PUT',
      headers: await getAuthHeaders(),
      body: JSON.stringify(fields),
    });
    if (!resp.ok) throw new Error(`Failed to update entry (${resp.status})`);
    return await resp.json();
  },

  async sync(fullRebuild: boolean = false): Promise<SyncResult> {
    const resp = await fetch(`${getBaseUrl()}/secure/blog/content-inventory/sync`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({ full_rebuild: fullRebuild }),
    });
    if (!resp.ok) {
      const text = await resp.text();
      throw new Error(`Sync failed: ${resp.status} ${text}`);
    }
    return await resp.json();
  },
};
